import { ClientSideOnly } from '@/components/clientSideOnly';
import { getAsQuery } from '@/components/filters/filterTools';
import {
  FallbackTableOptions,
  RowLinkOptions,
  SearchTable,
  useAvailableAndDefaultEnabledColumns,
  usePaginationState,
  useRowLink,
} from '@/components/searchTable';
import { SearchTableServerFallback } from '@/components/searchTable/table';
import { ViewHeader } from '@/components/ViewHeader';
import { useConfig } from '@/config/config';
import { FilterContext } from '@/contexts/filter';
import { useSearchContext } from '@/contexts/search';
import {
  OccurrenceSearchQuery,
  OccurrenceSearchQueryVariables,
  OccurrenceSortBy,
  SortOrder,
} from '@/gql/graphql';
import useQuery from '@/hooks/useQuery';
import { useI18n } from '@/reactRouterPlugins';
import { ExtractPaginatedResult } from '@/types';
import { notNull } from '@/utils/notNull';
import { useContext, useEffect, useMemo } from 'react';
import useLocalStorage from 'use-local-storage';
import { useFilters } from '../../filters';
import { searchConfig } from '../../searchConfig';
import { useEntityDrawer } from '../browseList/useEntityDrawer';
import { useOrderedList } from '../browseList/useOrderedList';
import { useOccurrenceColumns } from './columns';
import { useToast } from '@/components/ui/use-toast';

const MAX_RESULTS = 5000;

const OCCURRENCE_SEARCH_QUERY = /* GraphQL */ `
  query OccurrenceSearch(
    $from: Int
    $size: Int
    $predicate: Predicate
    $q: String
    $language: String
    $sortBy: OccurrenceSortBy
    $sortOrder: SortOrder
    $checklistKey: ID
  ) {
    occurrenceSearch(q: $q, predicate: $predicate) {
      documents(from: $from, size: $size, sortBy: $sortBy, sortOrder: $sortOrder) {
        from
        size
        total
        results {
          key
          taxonKey
          verbatimScientificName
          classification(checklistKey: $checklistKey) {
            usage {
              rank
              name
              key
            }
            taxonMatch {
              usage {
                name
                key
                canonicalName
                formattedName
              }
            }
            meta {
              mainIndex {
                datasetTitle
              }
            }
            vernacularNames(lang: $language, maxLimit: 1) {
              name
              reference {
                citation
              }
            }
            hasTaxonIssues
          }
          primaryImage {
            thumbor(width: 80)
          }
          eventDate
          year
          coordinates
          formattedCoordinates
          country
          countryCode
          basisOfRecord
          datasetTitle
          datasetKey
          publishingOrgKey
          publisherTitle
          catalogNumber
          recordedBy
          identifiedBy
          fieldNumber
          occurrenceRemarks(cap: 150)
          sex
          lifeStage
          recordNumber
          individualCount
          organismQuantity
          organismQuantityType
          typeStatus
          occurrenceStatus
          preparations
          institutionCode
          institutionKey
          institution {
            code
            name
          }
          collectionCode
          collectionKey
          collection {
            code
            name
          }
          organismID
          elevation
          depth
          locality
          higherGeography
          stateProvince
          establishmentMeans
          iucnRedListCategory
          stillImageCount
          movingImageCount
          soundCount
          issues(types: ["WARNING", "ERROR"])
          volatile {
            features {
              isSequenced
              isTreament
              isClustered
              isSamplingEvent
            }
          }
        }
      }
    }
  }
`;

export type SingleOccurrenceSearchResult = ExtractPaginatedResult<
  OccurrenceSearchQuery['occurrenceSearch']
>;

const keySelector = (item: SingleOccurrenceSearchResult) => item.key?.toString() ?? '';

const rowLinkOptionsDirect: RowLinkOptions<SingleOccurrenceSearchResult> = {
  pageId: 'occurrenceKey',
};

const rowLinkOptionsDrawer: RowLinkOptions<SingleOccurrenceSearchResult> = {
  createDrawerKey: ({ key }) => `o_${key}`,
};

const fallbackOptions: FallbackTableOptions = {
  prefixColumns: ['scientificName'],
  defaultEnabledTableColumns: [
    'features',
    'country',
    'coordinates',
    'year',
    'basisOfRecord',
    'datasetKey',
    'publishingOrg',
  ],
};

function getNotEmptyString(str: string | null | undefined): string | undefined {
  return str && str.length > 0 && typeof str === 'string' ? str : undefined;
}

export function OccurrenceTable() {
  return (
    <div className="g-flex g-flex-col g-h-full">
      <ClientSideOnly fallback={<SearchTableServerFallback />}>
        <OccurrenceTableClient />
      </ClientSideOnly>
    </div>
  );
}

export function OccurrenceTableClient() {
  const [occurrenceSort] = useLocalStorage<{ sortBy?: OccurrenceSortBy; sortOrder: SortOrder }>(
    'occurrenceSort',
    { sortBy: undefined, sortOrder: SortOrder.Asc }
  );
  const { translatedToast } = useToast();
  const { sortBy: storedSortBy, sortOrder: occurrenceSortOrder } = occurrenceSort;
  // A stored value from an older version may no longer be a valid sort field
  const occurrenceSortBy = Object.values(OccurrenceSortBy).includes(
    storedSortBy as OccurrenceSortBy
  )
    ? storedSortBy
    : undefined;
  const { locale } = useI18n();
  const searchContext = useSearchContext();
  const [paginationState, setPaginationState] = usePaginationState({
    pageSize: 50,
    maxResults: MAX_RESULTS,
  });
  const filterContext = useContext(FilterContext);

  if (
    paginationState.pageIndex * paginationState.pageSize + paginationState.pageSize >
    MAX_RESULTS
  ) {
    translatedToast({
      titleKey: 'error.maximumResultLimitExceeded',
      variant: 'destructive',
      titleValues: { MAX_RESULTS },
    });
  }

  const { filter, filterHash } = filterContext || { filter: { must: {} } };

  const { data, load, loading, error } = useQuery<
    OccurrenceSearchQuery,
    OccurrenceSearchQueryVariables
  >(OCCURRENCE_SEARCH_QUERY, {
    lazyLoad: true,
    keepDataWhileLoading: true,
  });

  useEffect(() => {
    const query = getAsQuery({ filter, searchContext, searchConfig });
    load({
      variables: {
        language: locale.iso3LetterCode,
        ...query,
        size: paginationState.pageSize,
        from: paginationState.pageIndex * paginationState.pageSize,
        sortBy: getNotEmptyString(occurrenceSortBy) as OccurrenceSortBy | undefined,
        sortOrder: getNotEmptyString(occurrenceSortOrder) as SortOrder | undefined,
      },
    });

    // We use a filterHash to trigger a reload when the filter changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    load,
    filterHash,
    searchContext,
    paginationState.pageIndex,
    paginationState.pageSize,
    occurrenceSort,
  ]);

  const { filters } = useFilters({ searchConfig });

  const [, showPreview] = useEntityDrawer();
  const columns = useOccurrenceColumns({
    showPreview,
  });
  if (typeof window !== 'undefined') {
    window.gbif = window.gbif || {};
    window.gbif.availableColumnOptions = columns.map((column) => column.id);
  }

  const occurrences = useMemo(
    () => data?.occurrenceSearch?.documents.results.filter(notNull) ?? [],
    [data]
  );

  const { setOrderedList } = useOrderedList();

  // update ordered list on items change
  useEffect(() => {
    setOrderedList(occurrences.map((item) => `o_${item.key}`));
  }, [occurrences, setOrderedList]);

  const { availableTableColumns, defaultEnabledTableColumns } =
    useAvailableAndDefaultEnabledColumns({
      searchMetadata: searchContext,
      columns,
      fallbackOptions,
    });

  const { openDrawerOnTableRowClick } = useConfig();
  const rowLinkOptions = openDrawerOnTableRowClick ? rowLinkOptionsDrawer : rowLinkOptionsDirect;

  const createRowLink = useRowLink({ rowLinkOptions, keySelector });

  if (!data?.occurrenceSearch?.documents && error) {
    throw error;
  }

  return (
    <>
      <ViewHeader
        className="g-flex"
        total={data?.occurrenceSearch?.documents.total}
        loading={loading}
        message="counts.nResults"
      >
        {/* {!loading && error && (
          <div className="g-ms-2 g-bg-red-400 g-text-white g-rounded g-px-1">
            Some rows where partially loaded
          </div>
        )} */}
      </ViewHeader>

      <SearchTable
        filters={filters}
        createRowLink={createRowLink}
        keySelector={keySelector}
        lockColumnLocalStoreKey="occurrenceSearchTableLockColumn"
        selectedColumnsLocalStoreKey="occurrenceSearchSelectedColumns"
        columns={columns}
        data={occurrences}
        loading={loading}
        rowCount={data?.occurrenceSearch?.documents.total}
        paginationState={paginationState}
        setPaginationState={setPaginationState}
        availableTableColumns={availableTableColumns}
        defaultEnabledTableColumns={defaultEnabledTableColumns}
      />
    </>
  );
}
